import React from 'react';
import { WidgetUi, WidgetUiProps } from 'common/ui';
import { MachineCardWidgetContainer, MachineCardWidgetHeader, ServiceStatusContainer } from './MachineCardWidget.elements';
import { Loader, useDateRange } from 'components';
import { useMachineKpi } from 'components/machine-health/MachineKpi/utils';
import { useMachineOverviewWidgetAssets } from 'common/pages/fleetV2/machine/protein/views/MachineHealth/components/MachineOverviewWidget/hooks/useMachineOverviewWidget';
import { useMachineUtilizationWidget } from 'common/pages/fleetV2/machine/protein/views/MachineHealth/components/MachineUtilizationWidget/hooks/useMachineUtilizationWidget';
import Wrench from 'icons/indexed-icons/Wrench';
import { getDurationInMs } from 'common/pages/fleetV2/machine/protein/views/MachineHealth/components/MachineUtilizationWidget/utils';
import TriangleWarning from 'icons/indexed-icons/TriangleWarning';
import { useMachineCardWidget } from './hooks/useMachineCardWidget';
import { NewRowRenderer } from 'common/pages/fleetV2/machine/protein/views/MachineHealth/components/RowRenderer/RowRenderer';
import theme from 'common/theme';

const pollingInterval = 120000;

export interface MachineCardWidgetProps extends WidgetUiProps {
  machineId?: string;
  name?: string;
  handleClick: (id: string) => void | undefined;
}

export const MachineCardWidget = ({
  machineId,
  name,
  handleClick,
}: MachineCardWidgetProps): JSX.Element => {

  const { dateRange } = useDateRange();
  const { main } = useMachineKpi(pollingInterval, undefined, machineId);
  const { machineAssets, machineAssetsIsLoading } = useMachineOverviewWidgetAssets({ machineId, pollingInterval });

  const {
    data: machineUtilizationData,
    isLoading: isMachineUtilizationDataLoading,
  } = useMachineUtilizationWidget({ machineId, pollingInterval });

  const { activeIssues } = useMachineCardWidget({ machineId, pollingInterval });

  //display default image or image from machineAssets
  const defaultImage = `/assets/placeholder/machines/dsi.jpg`;
  const imageToUse = machineAssets?.[machineAssets.length - 1]?.url || defaultImage;
  const Image = <img src={imageToUse} alt={machineId} className="machine-image" />;

  const rawServiceDuration = machineUtilizationData?.serviceDuration;
  const rawServiceTarget = machineUtilizationData?.serviceTarget;

  const serviceDuration = rawServiceDuration !== undefined
    ? `${Number(getDurationInMs('service', rawServiceDuration, dateRange.startTime, dateRange.endTime))}h`
    : '-';

  const durationValue = serviceDuration !== '-' ? parseFloat(serviceDuration) : null;
  const targetValue = rawServiceTarget !== undefined && rawServiceTarget !== null ? parseFloat(rawServiceTarget) : null;

  let serviceStatusColor = theme.colors.mainColor;
  let serviceStatusBackgroundColor = theme.colors.Blue200;
  
  if( durationValue === null || targetValue === null) {
    serviceStatusColor = theme.colors.mainColor;
    serviceStatusBackgroundColor = theme.colors.Blue200;
  } else if (durationValue < targetValue) {
    serviceStatusColor = theme.colors.darkRed;
    serviceStatusBackgroundColor = theme.colors.danger;
  } else if(durationValue >= targetValue){
    serviceStatusColor = theme.colors.green;
    serviceStatusBackgroundColor = 'rgb(0,130,0,0.2)';
  }

  const widgetSettings = {
    title: (
      <MachineCardWidgetHeader onClick={() => handleClick(machineId)}>
        {activeIssues && (<TriangleWarning color="#B30000" />)}
        <span>{name}</span>
      </MachineCardWidgetHeader>
    ),
  };

  return (
    <WidgetUi {...widgetSettings}>
      <MachineCardWidgetContainer>
        <div className="widget__image">
          {machineAssetsIsLoading ? <Loader size={50} /> : Image}
        </div>
        {isMachineUtilizationDataLoading ? (
            <Loader size={50} />
          ) : (
            <ServiceStatusContainer
              className='widget__service-status'
              serviceStatusColor={serviceStatusColor}
              serviceStatusBackgroundColor={serviceStatusBackgroundColor}
            >
                <p className="widget__service-status-text">
                <Wrench color={serviceStatusColor} />
                <span>Service Status:</span>
                </p>
              <p className='widget__service-status-value'>{serviceDuration} {(targetValue !== undefined && targetValue !== null) && `(Target ${targetValue}h)`}</p>
            </ServiceStatusContainer>
        )}
        <div className="widget__tags">
          <NewRowRenderer rows={main} isCustomRowHeight={true} customRowHeight='20px' lineView/>
        </div>
      </MachineCardWidgetContainer>
    </WidgetUi>
  );
};
